import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchMessageBody as apiFetchBody,
  fetchAttachments as apiFetchAttachments,
  markMessageRead as apiMarkRead,
} from '../services/graphMailService';
import { fetchThreadsByFolder, fetchMoreThreads } from './mailThreadsSlice';

// ---------------------------------------------------------------------------
// Async Thunks
// ---------------------------------------------------------------------------

export const fetchMessageBody = createAsyncThunk(
  'mailMessages/fetchBody',
  async ({ messageId }, { getState, rejectWithValue }) => {
    try {
      const existing = getState().mailMessages.byId[messageId];
      if (existing?.body?.loaded) return { messageId, body: existing.body };

      const data = await apiFetchBody(messageId);
      return { messageId, body: { ...data, loaded: true } };
    } catch (err) {
      return rejectWithValue({ messageId, error: err.message });
    }
  }
);

export const fetchAttachments = createAsyncThunk(
  'mailMessages/fetchAttachments',
  async ({ messageId }, { getState, rejectWithValue }) => {
    try {
      const existing = getState().mailMessages.byId[messageId];
      if (existing?.attachmentsLoaded) {
        return { messageId, attachments: existing.attachments };
      }
      const data = await apiFetchAttachments(messageId);
      return { messageId, attachments: data.attachments };
    } catch (err) {
      return rejectWithValue({ messageId, error: err.message });
    }
  }
);

export const markMessageReadAsync = createAsyncThunk(
  'mailMessages/markRead',
  async ({ messageId }, { rejectWithValue }) => {
    try {
      await apiMarkRead(messageId, true);
      return { messageId };
    } catch (err) {
      return rejectWithValue({ messageId, error: err.message });
    }
  }
);

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEFAULT_BODY = {
  content: '', contentType: 'html', loaded: false,
  blockedImageCount: 0, imagesUnblocked: false,
};

// ---------------------------------------------------------------------------
// Thread sort helper
// ---------------------------------------------------------------------------

/**
 * Canonical datetime value for a message: prefer sentDateTime, fall back to
 * receivedDateTime, then 0 (unknown messages sort to the bottom).
 */
function msgTime(msg) {
  const raw = msg?.sentDateTime || msg?.receivedDateTime;
  return raw ? new Date(raw).getTime() : 0;
}

/**
 * Sort an array of message IDs descending by msgTime (newest first).
 * Mutates the array in-place and returns it.
 */
function sortThreadIds(ids, byId) {
  ids.sort((a, b) => {
    const tA = msgTime(byId[a]);
    const tB = msgTime(byId[b]);
    return tB - tA; // descending — newest first
  });
  return ids;
}

// ---------------------------------------------------------------------------
// Reconciliation helper
// ---------------------------------------------------------------------------

/**
 * RECONCILIATION WINDOW: 120 seconds.
 *
 * When a confirmed message arrives for a conversation, we scan pendingById
 * for entries that match all three criteria:
 *   1. Same conversationId
 *   2. Status is 'sent' (API call has already returned 202)
 *   3. sentAt is within RECONCILE_WINDOW_MS of the confirmed message's
 *      receivedDateTime (Graph may take a few seconds to process)
 *   4. fromEmail matches confirmed message's from.email (prevents false positives
 *      from near-simultaneous messages by other participants)
 *
 * When matched: the pending entry is deleted. The confirmed message takes its
 * place in the UI via the normal byId / idsByThread path.
 *
 * WHY this works without a timeout hack:
 *   - The pending entry stays visible until Graph delivers the real message
 *     (via delta sync or fetchConversationMessages).
 *   - When the real message lands via upsertMessage or upsertMessages, the
 *     reconciliation runs synchronously in the reducer — zero extra dispatches.
 *   - The React key for the pending card is the stable clientId. When it
 *     disappears and the confirmed card (new key = real Graph ID) appears,
 *     React mounts the confirmed card. No flicker because the confirmed card
 *     has the same visual content.
 */
const RECONCILE_WINDOW_MS = 120_000;

function reconcilePending(state, incomingMessage) {
  const { conversationId, receivedDateTime, sentDateTime, from } = incomingMessage;
  if (!conversationId) return;

  const confirmedTime = new Date(receivedDateTime || sentDateTime || Date.now()).getTime();
  const fromEmail = from?.email || '';

  for (const clientId of Object.keys(state.pendingById)) {
    const pending = state.pendingById[clientId];

    if (
      pending.conversationId === conversationId &&
      pending.pendingStatus === 'sent' &&
      pending.fromEmail === fromEmail &&
      Math.abs(confirmedTime - new Date(pending.sentAt).getTime()) < RECONCILE_WINDOW_MS
    ) {
      delete state.pendingById[clientId];
      // Only one pending entry can reconcile per incoming message
      break;
    }
  }
}

// ---------------------------------------------------------------------------
// Initial State
// ---------------------------------------------------------------------------

const initialState = {
  // Confirmed messages from Graph: { [messageId]: Message }
  byId: {},

  // Per-thread ordered confirmed message IDs: { [conversationId]: string[] }
  // NOTE: pending messages are NOT added here. The selector merges both.
  idsByThread: {},

  // ─── Pending (optimistic) messages ────────────────────────────────────────
  //
  // Pending messages live here — completely separate from byId / idsByThread.
  // They are created before the Graph API returns, shown immediately in the
  // thread view with a "Sending…" indicator, and removed when a matching
  // confirmed message arrives (reconciliation) or when the send fails.
  //
  // Shape of each entry:
  //   clientId:      string   — uuid v4, stable across the send lifecycle
  //   pendingStatus: 'sending' | 'sent' | 'failed'
  //   conversationId: string
  //   fromEmail:     string   — used for reconciliation matching
  //   sentAt:        ISO      — used for reconciliation time window
  //   isPending:     true     — consumed by MessageCard to show sending indicator
  //   + all standard Message display fields for rendering
  //
  pendingById: {},
  // ──────────────────────────────────────────────────────────────────────────

  loadingBodyIds: [],
  draftIds: [],
  pendingSend: [],
};

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const mailMessagesSlice = createSlice({
  name: 'mailMessages',
  initialState,
  reducers: {
    // -----------------------------------------------------------------------
    // Confirmed messages (from Graph)
    // -----------------------------------------------------------------------

    upsertMessages(state, action) {
      const messages = action.payload; // Message[]
      messages.forEach((msg) => {
        const existing = state.byId[msg.id];
        state.byId[msg.id] = {
          ...existing,
          ...msg,
          body: existing?.body?.loaded
            ? existing.body
            : DEFAULT_BODY,
          bodyStatus: existing?.body?.loaded ? 'loaded' : 'not-loaded',
          attachments: existing?.attachments || [],
          attachmentsLoaded: existing?.attachmentsLoaded || false,
        };

        const convId = msg.conversationId;
        if (!state.idsByThread[convId]) state.idsByThread[convId] = [];
        if (!state.idsByThread[convId].includes(msg.id)) {
          state.idsByThread[convId].push(msg.id);
        }

        // Attempt to reconcile against any pending entry for this conversation
        reconcilePending(state, msg);
      });

      // Sort each affected thread descending (newest first)
      Object.keys(state.idsByThread).forEach((convId) => {
        sortThreadIds(state.idsByThread[convId], state.byId);
      });
    },

    upsertMessage(state, action) {
      const msg = action.payload;
      const existing = state.byId[msg.id];
      const bodyLoaded = existing?.body?.loaded || msg.body?.loaded || false;
      state.byId[msg.id] = {
        ...existing,
        ...msg,
        body: bodyLoaded
          ? (existing?.body?.loaded ? existing.body : msg.body)
          : DEFAULT_BODY,
        bodyStatus: bodyLoaded ? 'loaded' : 'not-loaded',
        attachments: existing?.attachments || msg.attachments || [],
        attachmentsLoaded: existing?.attachmentsLoaded || msg.attachmentsLoaded || false,
      };

      const convId = msg.conversationId;
      if (!state.idsByThread[convId]) state.idsByThread[convId] = [];
      if (!state.idsByThread[convId].includes(msg.id)) {
        state.idsByThread[convId].push(msg.id);
      }
      sortThreadIds(state.idsByThread[convId], state.byId);

      // Attempt to reconcile against any pending entry for this conversation
      reconcilePending(state, msg);
    },

    removeMessage(state, action) {
      const { messageId, conversationId } = action.payload;
      delete state.byId[messageId];
      if (state.idsByThread[conversationId]) {
        state.idsByThread[conversationId] = state.idsByThread[conversationId].filter(
          (id) => id !== messageId
        );
        if (state.idsByThread[conversationId].length === 0) {
          delete state.idsByThread[conversationId];
        }
      }
    },

    markMessageRead(state, action) {
      const { messageId } = action.payload;
      if (state.byId[messageId]) state.byId[messageId].isRead = true;
    },

    rollbackMarkRead(state, action) {
      const { messageId } = action.payload;
      if (state.byId[messageId]) state.byId[messageId].isRead = false;
    },

    setMessageFlag(state, action) {
      const { messageId, flagStatus } = action.payload;
      if (state.byId[messageId]) state.byId[messageId].flag = { flagStatus };
    },

    unblockImages(state, action) {
      const { messageId } = action.payload;
      if (state.byId[messageId]?.body) {
        state.byId[messageId].body.imagesUnblocked = true;
        state.byId[messageId].body.blockedImageCount = 0;
      }
    },

    evictMessageBody(state, action) {
      const { messageId } = action.payload;
      if (state.byId[messageId]) {
        state.byId[messageId].body = {
          content: '', contentType: 'html', loaded: false,
          blockedImageCount: 0, imagesUnblocked: false,
        };
        state.byId[messageId].bodyStatus = 'not-loaded';
      }
    },

    addDraftId(state, action) {
      const { messageId } = action.payload;
      if (!state.draftIds.includes(messageId)) state.draftIds.push(messageId);
    },

    removeDraftId(state, action) {
      const { messageId } = action.payload;
      state.draftIds = state.draftIds.filter((id) => id !== messageId);
    },

    // -----------------------------------------------------------------------
    // Pending message lifecycle
    // -----------------------------------------------------------------------

    /**
     * Add an optimistic pending message immediately when the user hits Send.
     *
     * The message shape must include all fields needed by MessageCard so it
     * renders identically to a confirmed message, plus:
     *   - clientId:      stable uuid v4 (used as React key + reconciliation ID)
     *   - pendingStatus: 'sending'
     *   - isPending:     true  (MessageCard checks this to show sending indicator)
     *   - sentAt:        ISO timestamp of when the send was triggered
     *   - fromEmail:     sender's email address (for reconciliation matching)
     */
    addPendingMessage(state, action) {
      const { clientId, message } = action.payload;
      state.pendingById[clientId] = {
        ...message,
        id: clientId,              // React key = clientId
        clientId,
        isPending: true,
        pendingStatus: 'sending',
      };
    },

    /**
     * Mark pending as 'sent' — API returned 202, awaiting real message from Graph.
     *
     * Status changes from 'sending' → 'sent'. This is the trigger condition for
     * reconciliation: only 'sent' entries are matched against incoming confirmed
     * messages. 'sending' entries are never reconciled (race: API might fail).
     */
    markPendingSent(state, action) {
      const { clientId } = action.payload;
      if (state.pendingById[clientId]) {
        state.pendingById[clientId].pendingStatus = 'sent';
      }
    },

    /**
     * Mark pending as 'failed' — API returned an error.
     *
     * The message stays visible in the thread with a failed/retry indicator.
     * It is NOT auto-removed — the user must explicitly dismiss or retry.
     * Failed messages are excluded from the reconciliation scan.
     */
    markPendingFailed(state, action) {
      const { clientId } = action.payload;
      if (state.pendingById[clientId]) {
        state.pendingById[clientId].pendingStatus = 'failed';
      }
    },

    /**
     * Explicitly remove a pending message (user dismisses a failed send, or
     * manual cleanup after a confirmed message arrives via a different path).
     */
    removePendingMessage(state, action) {
      const { clientId } = action.payload;
      delete state.pendingById[clientId];
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchMessageBody.pending, (state, action) => {
        const { messageId } = action.meta.arg;
        if (state.byId[messageId]) state.byId[messageId].bodyStatus = 'loading';
        if (!state.loadingBodyIds.includes(messageId)) state.loadingBodyIds.push(messageId);
      })
      .addCase(fetchMessageBody.fulfilled, (state, action) => {
        const { messageId, body } = action.payload;
        if (state.byId[messageId]) {
          state.byId[messageId].body = body;
          state.byId[messageId].bodyStatus = 'loaded';
        }
        state.loadingBodyIds = state.loadingBodyIds.filter((id) => id !== messageId);
      })
      .addCase(fetchMessageBody.rejected, (state, action) => {
        const { messageId } = action.payload || action.meta.arg;
        if (state.byId[messageId]) state.byId[messageId].bodyStatus = 'error';
        state.loadingBodyIds = state.loadingBodyIds.filter((id) => id !== messageId);
      });

    builder.addCase(fetchAttachments.fulfilled, (state, action) => {
      const { messageId, attachments } = action.payload;
      if (state.byId[messageId]) {
        state.byId[messageId].attachments = attachments;
        state.byId[messageId].attachmentsLoaded = true;
      }
    });

    builder.addCase(markMessageReadAsync.rejected, (state, action) => {
      const { messageId } = action.payload || action.meta.arg;
      if (state.byId[messageId]) state.byId[messageId].isRead = false;
    });

    const ingestMessages = (state, messages = []) => {
      const affectedConvIds = new Set();
      messages.forEach((msg) => {
        const existing = state.byId[msg.id];
        state.byId[msg.id] = {
          ...existing,
          ...msg,
          body: existing?.body?.loaded ? existing.body : {
            content: '', contentType: msg.body?.contentType || 'html',
            loaded: false, blockedImageCount: 0, imagesUnblocked: false,
          },
          bodyStatus: existing?.body?.loaded ? 'loaded' : 'not-loaded',
          attachments: existing?.attachments || [],
          attachmentsLoaded: existing?.attachmentsLoaded || false,
        };
        const convId = msg.conversationId;
        if (!state.idsByThread[convId]) state.idsByThread[convId] = [];
        if (!state.idsByThread[convId].includes(msg.id)) {
          state.idsByThread[convId].push(msg.id);
        }
        affectedConvIds.add(convId);
        reconcilePending(state, msg);
      });
      // Sort each affected thread descending (newest first)
      affectedConvIds.forEach((convId) => {
        sortThreadIds(state.idsByThread[convId], state.byId);
      });
    };

    builder
      .addCase(fetchThreadsByFolder.fulfilled, (state, action) => {
        ingestMessages(state, action.payload.messages);
      })
      .addCase(fetchMoreThreads.fulfilled, (state, action) => {
        ingestMessages(state, action.payload.messages);
      });
  },
});

export const {
  upsertMessages,
  upsertMessage,
  removeMessage,
  markMessageRead,
  rollbackMarkRead,
  setMessageFlag,
  unblockImages,
  evictMessageBody,
  addDraftId,
  removeDraftId,
  // Pending lifecycle
  addPendingMessage,
  markPendingSent,
  markPendingFailed,
  removePendingMessage,
} = mailMessagesSlice.actions;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export const selectMessageById = (state, messageId) =>
  state.mailMessages.byId[messageId];

/**
 * Returns all messages for a thread sorted newest-first (descending), matching
 * real Outlook's thread display order.
 *
 * - Confirmed messages come from idsByThread (pre-sorted descending in reducers).
 * - Pending messages (isPending: true) are merged in and sorted by the same key.
 *   They are typically the very newest entries (just sent).
 * - Failed pending messages (pendingStatus: 'failed') ARE included so the
 *   user can see and retry them. Components check msg.pendingStatus === 'failed'.
 *
 * Sort key: sentDateTime || receivedDateTime || sentAt (for pending), descending.
 * messages[0] is always the latest; messages[length-1] is always the oldest.
 */
export const selectMessagesByThread = (state, conversationId) => {
  if (!conversationId) return [];

  const ids = state.mailMessages.idsByThread[conversationId] || [];
  const confirmed = ids
    .map((id) => state.mailMessages.byId[id])
    .filter(Boolean);

  const pending = Object.values(state.mailMessages.pendingById)
    .filter((p) => p.conversationId === conversationId);

  // Always sort — never rely on reducer-level ordering being correct
  // (stale store data loaded before a sort fix won't be re-sorted by reducers)
  const all = pending.length ? [...confirmed, ...pending] : [...confirmed];
  all.sort((a, b) => {
    const tA = new Date(a.sentDateTime || a.receivedDateTime || a.sentAt || 0).getTime();
    const tB = new Date(b.sentDateTime || b.receivedDateTime || b.sentAt || 0).getTime();
    return tB - tA; // descending — newest first, messages[0] = latest
  });
  return all;
};

export const selectPendingMessagesByThread = (state, conversationId) =>
  Object.values(state.mailMessages.pendingById).filter(
    (p) => p.conversationId === conversationId
  );

export const selectMessageBodyStatus = (state, messageId) =>
  state.mailMessages.byId[messageId]?.bodyStatus || 'not-loaded';

export const selectIsBodyLoading = (state, messageId) =>
  state.mailMessages.loadingBodyIds.includes(messageId);

export const selectLatestMessageInThread = (state, conversationId) => {
  const all = selectMessagesByThread(state, conversationId);
  return all.length ? all[0] : null; // messages[0] is newest (descending sort)
};

export default mailMessagesSlice.reducer;
